import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../context/AuthContext'
import { AlertTriangle, Target, ChevronLeft, ChevronRight, Trophy, Flame, BarChart2, X, Briefcase, Star, Clock, TrendingUp, User } from 'lucide-react'
import api from '../api/axios'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

function LeaderboardHistory() {
  const { hasPermission, user } = useAuth()
  const canDownload = !['artist', 'selfqc', 'qc'].includes(user?.roleName || '')
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [prevData, setPrevData] = useState<any>(null)

  const fetchLeaderboard = async (m: number, y: number) => {
    setLoading(true)
    try {
      const res = await api.get(`/performance/leaderboard?period=monthly&month=${m}&year=${y}`)
      setData(res.data)
      // Fetch previous month for comparison
      const prevM = m === 1 ? 12 : m - 1
      const prevY = m === 1 ? y - 1 : y
      const prevRes = await api.get(`/performance/leaderboard?period=monthly&month=${prevM}&year=${prevY}`)
      setPrevData(prevRes.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchLeaderboard(month, year); setLbPage(1) }, [month, year])

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1) }
    else setMonth(m => m - 1)
  }
  const nextMonth = () => {
    const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear()
    if (isCurrentMonth) return
    if (month === 12) { setMonth(1); setYear(y => y + 1) }
    else setMonth(m => m + 1)
  }

  const getPrevRank = (artistId: string) => {
    if (!prevData) return null
    const prev = prevData.leaderboard.find((e: any) => e.artistId === artistId)
    return prev ? prev.rank : null
  }

  const getRankChange = (artistId: string, currentRank: number) => {
    const prevRank = getPrevRank(artistId)
    if (!prevRank) return null
    if (prevRank > currentRank) return { dir: 'up', diff: prevRank - currentRank }
    if (prevRank < currentRank) return { dir: 'down', diff: currentRank - prevRank }
    return { dir: 'same', diff: 0 }
  }

  const maxCredits = data?.leaderboard?.[0]?.netCredits || 1
  const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear()
  const [lbPage, setLbPage] = useState(1)
  const PAGE_SIZE = 7
  const totalLbPages = Math.ceil((data?.leaderboard?.length || 0) / PAGE_SIZE)
  const pagedLeaderboard = (data?.leaderboard || []).slice((lbPage - 1) * PAGE_SIZE, lbPage * PAGE_SIZE)

  const downloadXLSX = () => {
    if (!data?.leaderboard?.length) return
    import('xlsx').then(XLSX => {
      const rows = data.leaderboard.map((e: any) => ({
        'Rank': e.rank,
        'Artist': e.name,
        'Net Credits': e.netCredits,
        'Jobs': e.jobCount,
        'Streak': e.currentStreak,
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      ws['!cols'] = Object.keys(rows[0]).map((k: string) => ({ wch: Math.max(k.length, 12) }))
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Leaderboard')
      XLSX.writeFile(wb, `leaderboard-${monthNames[month-1]}-${year}.xlsx`)
    })
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-display font-semibold text-navy-900 dark:text-white flex items-center gap-2"><Trophy size={16} className="text-yellow-500" /> Leaderboard History</h2>
          <span className="text-xs text-gray-400">Monthly rankings</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Month selector */}
          <div className="flex items-center gap-1">
            <button onClick={prevMonth} className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1e3a5f] hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors">
              <ChevronLeft size={14} />
            </button>
            <span className="text-sm font-semibold text-navy-800 dark:text-white w-24 text-center">
              {monthNames[month-1]} {year}
            </span>
            <button onClick={nextMonth} disabled={isCurrentMonth}
              className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1e3a5f] hover:bg-gray-50 dark:hover:bg-[#1a3050] disabled:opacity-40 transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>
          {/* Export */}
          {canDownload && <button onClick={downloadXLSX} className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1e3a5f] text-gray-400 hover:text-green-600 hover:border-green-200 transition-colors" title="Download Excel">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </button>}
        </div>
      </div>

      {loading ? (
        <div className="p-6 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="skeleton w-8 h-8 rounded-full" />
              <div className="flex-1 skeleton h-4" />
              <div className="skeleton h-4 w-20" />
            </div>
          ))}
        </div>
      ) : !data?.leaderboard?.length ? (
        <div className="py-12 text-center text-gray-400">
          <div className="mb-2"><Trophy size={28} className="text-yellow-400 mx-auto" /></div>
          <p>No leaderboard data for {monthNames[month-1]} {year}</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50 dark:divide-[#1a3050]">
          {pagedLeaderboard.map((entry: any) => {
            const rankChange = getRankChange(entry.artistId, entry.rank)
            const barWidth = Math.round((entry.netCredits / maxCredits) * 100)
            const medal = entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : null
            return (
              <div key={entry.artistId} className="px-6 py-3 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors">
                {/* Rank */}
                <div className="w-8 text-center flex-shrink-0">
                  {medal ? (
                    <span className="text-lg">{medal}</span>
                  ) : (
                    <span className="text-sm font-bold text-gray-400">#{entry.rank}</span>
                  )}
                </div>
                {/* Artist */}
                <div className="flex items-center gap-2 w-36 flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${
                    entry.rank === 1 ? 'bg-yellow-400' : entry.rank === 2 ? 'bg-gray-400' : entry.rank === 3 ? 'bg-orange-400' : 'bg-navy-400'
                  }`}>
                    {entry.name[0]}
                  </div>
                  <span className="text-sm font-medium text-navy-800 dark:text-white truncate">{entry.name.split(' ')[0]}</span>
                </div>
                {/* Bar */}
                <div className="flex-1 h-1.5 bg-gray-100 dark:bg-[#1a3050] rounded-full overflow-hidden">
                  <div className={`h-1.5 rounded-full ${
                    entry.rank === 1 ? 'bg-yellow-400' : entry.rank === 2 ? 'bg-gray-400' : entry.rank === 3 ? 'bg-orange-400' : 'bg-blue-400'
                  }`} style={{ width: `${barWidth}%` }} />
                </div>
                {/* Credits */}
                <div className="text-right w-20 flex-shrink-0">
                  <p className="text-sm font-bold text-navy-800 dark:text-white">{entry.netCredits}</p>
                  <p className="text-[10px] text-gray-400">{entry.jobCount} jobs</p>
                </div>
                {/* Streak */}
                {entry.currentStreak > 0 && (
                  <div className="text-xs text-orange-500 font-medium w-12 flex-shrink-0 flex items-center gap-0.5"><Flame size={11} /> {entry.currentStreak}</div>
                )}
                {/* Rank change vs prev month */}
                {rankChange && (
                  <div className={`text-xs font-semibold w-10 flex-shrink-0 flex items-center gap-0.5 ${
                    rankChange.dir === 'up' ? 'text-green-500' : rankChange.dir === 'down' ? 'text-red-400' : 'text-gray-300'
                  }`}>
                    {rankChange.dir === 'up' ? '↑' : rankChange.dir === 'down' ? '↓' : '—'}
                    {rankChange.diff > 0 && rankChange.diff}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      {totalLbPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
          <p className="text-xs text-gray-400">{data?.leaderboard?.length} artists — page {lbPage} of {totalLbPages}</p>
          <div className="flex gap-2">
            <button disabled={lbPage === 1} onClick={() => setLbPage(p => p - 1)}
              className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1e3a5f] disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-[#1a3050]">
              <ChevronLeft size={14} />
            </button>
            <button disabled={lbPage >= totalLbPages} onClick={() => setLbPage(p => p + 1)}
              className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1e3a5f] disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-[#1a3050]">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function PerformancePage() {
  const { user, hasPermission } = useAuth()
  const isAdmin = user?.roleName === 'admin'
  const canSeeAll = isAdmin || hasPermission('view_performance_all')

  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [errorPage, setErrorPage] = useState(1)
  const [selectedStaff, setSelectedStaff] = useState<string[]>([])
  const errorPageSize = 7
  const [staffList, setStaffList] = useState<any[]>([])
  const [dayJobs, setDayJobs] = useState<any[]>([])
  const [dayJobsLoading, setDayJobsLoading] = useState(false)
  const [selectedDay, setSelectedDay] = useState<any>(null)
  const [leaderboard, setLeaderboard] = useState<any[]>([])
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<'weekly' | 'monthly'>('weekly')
  const [leaderboardPage, setLeaderboardPage] = useState(1)
  const LEADERBOARD_PAGE_SIZE = 7
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true)
  const [myStats, setMyStats] = useState<any>(null)
  const [personalStatsEnabled, setPersonalStatsEnabled] = useState(true)
  const isArtist = hasPermission('can_work_on_jobs') && !hasPermission('exclude_from_leaderboard')
  const [activeTab, setActiveTab] = useState<'leaderboard' | 'staffcard' | 'history' | 'performance'>('leaderboard')
  const [scStaff, setScStaff] = useState('')
  const [scMonth, setScMonth] = useState(new Date().getMonth() + 1)
  const [scYear, setScYear] = useState(new Date().getFullYear())
  const [scData, setScData] = useState<any>(null)
  const [scLoading, setScLoading] = useState(false)
  const [scOpen, setScOpen] = useState(false)

  const fetchData = async () => {
    setLoading(true)
    try {
      let url = canSeeAll ? '/performance/all' : '/performance/me'
      const params = new URLSearchParams()
      if (from) params.append('from', from)
      if (to) params.append('to', to)
      params.append('page', page.toString())
      if (canSeeAll && selectedStaff.length === 1) params.append('userId', selectedStaff[0])
      // For multiple selection, fetch all and filter client-side
      url += '?' + params.toString()
      const res = await api.get(url)
      if (res.data.staff?.length) setStaffList(res.data.staff)
      if (canSeeAll && selectedStaff.length > 1 && res.data.dailyList) {
        res.data.dailyList = res.data.dailyList.filter((d: any) =>
          selectedStaff.some(id => staffList.find((s: any) => s.id === id && s.name === d.staffName))
        )
      }
      setData(res.data)
    } catch {}
    setLoading(false)
  }

  const downloadXLSX = async () => {
    try {
      const params = new URLSearchParams()
      if (from) params.append('from', from)
      if (to) params.append('to', to)
      params.append('limit', '1000')
      if (canSeeAll && selectedStaff.length === 1) params.append('userId', selectedStaff[0])
      const url = canSeeAll ? `/performance/all?${params}` : `/performance/me?${params}`
      const res = await api.get(url)
      const list = res.data.dailyList || []
      const data = list.map((d: any) => ({
        'Date': new Date(d.date + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
        'Staff': d.staffName || '—',
        'Net Credits': d.netCredits,
        'Gross Credits': d.grossCredits,
        'Target %': d.targetPct + '%',
        'Loss': d.loss,
      }))
      const ws = XLSX.utils.json_to_sheet(data)
      ws['!cols'] = Object.keys(data[0] || {}).map(key => ({
        wch: Math.max(key.length, ...data.map((r: any) => String(r[key] || '').length))
      }))
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Performance')
      XLSX.writeFile(wb, `performance-${new Date().toISOString().split('T')[0]}.xlsx`)
    } catch (e) { console.error(e) }
  }

  const downloadPDF = async () => {
    try {
      // Load logo as base64
      let logoBase64 = ''
      try {
        const logoRes = await fetch('/Full_Color_Logo.svg')
        const blob = await logoRes.blob()
        logoBase64 = await new Promise<string>(resolve => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(blob)
        })
      } catch {}

      const params = new URLSearchParams()
      if (from) params.append('from', from)
      if (to) params.append('to', to)
      params.append('limit', '1000')
      if (canSeeAll && selectedStaff.length === 1) params.append('userId', selectedStaff[0])
      const url = canSeeAll ? `/performance/all?${params}` : `/performance/me?${params}`
      const res = await api.get(url)
      const list = res.data.dailyList || []
      const summary = res.data.summary

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pageW = doc.internal.pageSize.getWidth()

      // Header
      doc.setFillColor(18, 61, 104)
      doc.rect(0, 0, pageW, 35, 'F')

      // Logo on left
      if (logoBase64) {
        const logoW = 44
        const logoH = logoW * (50 / 201)
        doc.addImage(logoBase64, 'PNG', 10, 8, logoW, logoH)
      }

      // Title on right
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Artist Performance Report', pageW - 14, 14, { align: 'right' })
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      const period = from && to ? `${from} to ${to}` : 'All Time'
      doc.text(`Period: ${period}`, pageW - 14, 21, { align: 'right' })
      doc.text(`Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`, pageW - 14, 27, { align: 'right' })

      // Summary cards
      if (summary) {
        doc.setTextColor(0, 0, 0)
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.text('Summary', 14, 47)

        const cards = [
          { label: 'Avg Efficiency', value: `${summary.avgTargetPct}%` },
          { label: 'Avg Daily Credits', value: `${summary.avgNet}` },
          { label: 'Total Days', value: `${list.length}` },
          { label: 'Daily Target', value: `${summary.dailyTarget} credits` },
        ]
        cards.forEach((card, i) => {
          const x = 14 + (i % 2) * 90
          const y = 53 + Math.floor(i / 2) * 16
          doc.setFillColor(245, 247, 250)
          doc.roundedRect(x, y, 85, 12, 2, 2, 'F')
          doc.setFontSize(8)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(100, 100, 100)
          doc.text(card.label, x + 4, y + 5)
          doc.setFontSize(10)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(18, 61, 104)
          doc.text(card.value, x + 4, y + 10)
        })
      }

      // Table
      doc.setTextColor(0, 0, 0)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.text('Daily Performance', 14, 91)

      autoTable(doc, {
        startY: 95,
        head: [['Date', 'Staff', 'Net Credits', 'Gross Credits', 'Target %', 'Loss']],
        body: list.map((d: any) => [
          new Date(d.date + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
          d.staffName || '—',
          d.netCredits,
          d.grossCredits,
          `${d.targetPct}%`,
          d.loss || 0
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [18, 61, 104], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 35 },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' }
        }
      })

      const staffName = canSeeAll && selectedStaff.length === 1
        ? staffList.find((s: any) => s.id === selectedStaff[0])?.name || 'staff'
        : user?.name || 'performance'
      doc.save(`performance-${staffName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`)
    } catch (e) { console.error('PDF export failed', e) }
  }

  const setToday = () => {
    const today = new Date().toLocaleDateString('en-CA')
    setFrom(today)
    setTo(today)
    setPage(1)
    setErrorPage(1)
  }

  const setLast30 = () => {
    const now = new Date()
    const past = new Date(now)
    past.setDate(past.getDate() - 30)
    setFrom(past.toLocaleDateString('en-CA'))
    setTo(now.toLocaleDateString('en-CA'))
    setPage(1)
    setErrorPage(1)
  }

  useEffect(() => { fetchData() }, [from, to, page, selectedStaff])

  const handleDayClick = async (d: any) => {
    setSelectedDay(d)
    setDayJobsLoading(true)
    setDayJobs([])
    try {
      const params = new URLSearchParams()
      params.append('dateFrom', d.date)
      params.append('dateTo', d.date)
      if (d.staffId) params.append('userId', d.staffId)
      const res = await api.get(`/performance/me?${params.toString()}`)
      setDayJobs(res.data.dailyList?.[0]?.jobs || [])
    } catch {}
    setDayJobsLoading(false)
  }
  useEffect(() => {
    if (canSeeAll) {
      api.get('/performance/all?page=1&limit=1').then(res => {
        if (res.data.staff?.length) setStaffList(res.data.staff)
      }).catch(() => {})
    }
  }, [canSeeAll])

  useEffect(() => {
    api.get(`/performance/leaderboard?period=${leaderboardPeriod}`).then(res => {
      setLeaderboard(res.data.leaderboard || [])
      setLeaderboardPage(1)
    }).catch(() => {})
    api.get('/settings/public/leaderboard_enabled').then(res => setLeaderboardEnabled(res.data.value !== 'false')).catch(() => {})
    api.get('/settings/public/personal_stats_enabled').then(res => setPersonalStatsEnabled(res.data.value !== 'false')).catch(() => {})
    if (!canSeeAll || isArtist) {
      api.get('/performance/my-stats').then(res => setMyStats(res.data)).catch(() => {})
    }
  }, [leaderboardPeriod])

  const summary = data?.summary
  const chartData = data?.chartData || []
  const dailyList = data?.dailyList || []
  const allErrors = data?.errors || []
  const staff = data?.staff || []
  const pagination = data?.pagination

  const errorTotal = allErrors.length
  const errorPages = Math.ceil(errorTotal / errorPageSize)
  const pagedErrors = allErrors.slice((errorPage - 1) * errorPageSize, errorPage * errorPageSize)

  const chartH = 180
  const maxVal = 125 // fixed scale for target % chart (capped at 125%)
  const barW = Math.min(36, Math.max(12, 560 / Math.max(chartData.length, 1) - 6))
  const chartTotalW = Math.max(600, chartData.length * (barW + 6) + 60)

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6">
      {summary && (
        <p className="text-sm text-gray-400 -mt-2">
          Daily Target: <span className="font-semibold text-navy-700 dark:text-gray-200">{summary.dailyTarget} credits</span>
          &nbsp;·&nbsp;
          Penalty Rate: <span className="font-semibold text-red-500">{summary.penaltyRate}%</span>
        </p>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 dark:bg-[#1a3050] rounded-xl p-1 w-fit">
        {leaderboardEnabled && (
          <button onClick={() => setActiveTab('leaderboard')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'leaderboard' ? 'bg-white dark:bg-[#0d1e35] text-navy-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            Leaderboard
          </button>
        )}
        {hasPermission('view_staff_performance_card') && (
          <button onClick={() => setActiveTab('staffcard')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'staffcard' ? 'bg-white dark:bg-[#0d1e35] text-navy-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            Staff Card
          </button>
        )}
        <button onClick={() => setActiveTab('history')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'history' ? 'bg-white dark:bg-[#0d1e35] text-navy-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          History
        </button>
        <button onClick={() => setActiveTab('performance')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'performance' ? 'bg-white dark:bg-[#0d1e35] text-navy-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Performance
        </button>
      </div>

      {/* Leaderboard */}
      {activeTab === 'leaderboard' && leaderboardEnabled && <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
          <div>
            <p className="font-display font-bold text-navy-900 dark:text-white flex items-center gap-2"><Trophy size={16} className="text-yellow-500" /> Leaderboard</p>
            <p className="text-xs text-gray-400">Ranked by net credits earned</p>
          </div>
          <div className="flex gap-1 bg-gray-100 dark:bg-[#1a3050] rounded-xl p-1">
            {(['weekly', 'monthly'] as const).map(p => (
              <button key={p} onClick={() => setLeaderboardPeriod(p)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors capitalize ${leaderboardPeriod === p ? 'bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
          {leaderboard.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">No data for this period</div>
          ) : leaderboard.slice((leaderboardPage - 1) * LEADERBOARD_PAGE_SIZE, leaderboardPage * LEADERBOARD_PAGE_SIZE).map((entry, i) => (
            <div key={entry.artistId} className={`flex items-center gap-4 px-5 py-3.5 ${entry.artistId === user?.userId ? 'bg-orange-50 dark:bg-orange-900/10' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                entry.rank === 1 ? 'bg-yellow-100 text-yellow-600' :
                entry.rank === 2 ? 'bg-gray-100 text-gray-500' :
                entry.rank === 3 ? 'bg-orange-100 text-orange-600' :
                'bg-gray-50 dark:bg-[#1a3050] text-gray-400'
              }`}>
                {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-navy-800 dark:text-white truncate">
                    {entry.name} {entry.artistId === user?.userId ? '(you)' : ''}
                  </p>
                  {entry.currentStreak >= 3 && (
                    <span className="text-xs text-orange-500 font-medium flex items-center gap-0.5"><Flame size={11} /> {entry.currentStreak}</span>
                  )}
                </div>
                <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-full h-1.5 mt-1">
                  <div className="bg-orange-400 h-1.5 rounded-full" style={{ width: `${Math.min(100, (entry.netCredits / (leaderboard[0]?.netCredits || 1)) * 100)}%` }} />
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-navy-800 dark:text-white">{entry.netCredits}</p>
                <p className="text-xs text-gray-400">{entry.jobCount} jobs</p>
              </div>
            </div>
          ))}
        </div>
        {leaderboard.length > LEADERBOARD_PAGE_SIZE && (
          <div className="px-5 py-3 border-t border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Showing {(leaderboardPage - 1) * LEADERBOARD_PAGE_SIZE + 1}–{Math.min(leaderboardPage * LEADERBOARD_PAGE_SIZE, leaderboard.length)} of {leaderboard.length}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setLeaderboardPage(p => Math.max(1, p - 1))} disabled={leaderboardPage === 1}
                className="w-7 h-7 rounded-lg border border-gray-200 dark:border-[#1e3a5f] flex items-center justify-center text-gray-400 hover:text-navy-700 disabled:opacity-40 transition-colors">
                ‹
              </button>
              <span className="text-xs text-gray-500">{leaderboardPage} / {Math.ceil(leaderboard.length / LEADERBOARD_PAGE_SIZE)}</span>
              <button onClick={() => setLeaderboardPage(p => Math.min(Math.ceil(leaderboard.length / LEADERBOARD_PAGE_SIZE), p + 1))} disabled={leaderboardPage >= Math.ceil(leaderboard.length / LEADERBOARD_PAGE_SIZE)}
                className="w-7 h-7 rounded-lg border border-gray-200 dark:border-[#1e3a5f] flex items-center justify-center text-gray-400 hover:text-navy-700 disabled:opacity-40 transition-colors">
                ›
              </button>
            </div>
          </div>
        )}
      </div>}

      {/* Personal Stats Card */}
      {personalStatsEnabled && myStats && (
        <div className="card p-5 space-y-5">
          <p className="font-display font-bold text-navy-900 dark:text-white flex items-center gap-2"><BarChart2 size={16} className="text-indigo-500" /> My Career Stats</p>

          {/* Top stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Jobs', value: myStats.totalJobs, icon: <Briefcase size={20} className="text-blue-500 mx-auto" /> },
              { label: 'Total Credits', value: myStats.totalCredits, icon: <Star size={20} className="text-yellow-500 mx-auto" /> },
              { label: 'Total Hours', value: `${myStats.totalHours}h`, icon: <Clock size={20} className="text-indigo-500 mx-auto" /> },
              { label: 'Avg Credits/Day', value: myStats.avgCreditsPerDay, icon: <TrendingUp size={20} className="text-green-500 mx-auto" /> },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 dark:bg-[#1a3050] rounded-xl p-3 text-center">
                <div className="mb-1">{s.icon}</div>
                <p className="text-lg font-bold text-navy-900 dark:text-white">{s.value}</p>
                <p className="text-xs text-gray-400">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Streak */}
          <div className="flex items-center gap-4 bg-orange-50 dark:bg-orange-900/10 rounded-xl px-4 py-3">
            <Flame size={22} className="text-orange-500" />
            <div>
              <p className="text-sm font-semibold text-navy-800 dark:text-white">Current Streak: {myStats.currentStreak} days</p>
              <p className="text-xs text-gray-400">Best streak: {myStats.bestStreak} days</p>
            </div>
          </div>

          {/* Monthly credits chart */}
          {myStats.monthlyCredits?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Credits — Last 6 Months</p>
              <div className="flex items-end gap-2 h-24">
                {myStats.monthlyCredits.map((m: any) => {
                  const max = Math.max(...myStats.monthlyCredits.map((x: any) => x.credits), 1)
                  const pct = Math.round((m.credits / max) * 100)
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                      <p className="text-xs text-gray-400">{m.credits}</p>
                      <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-t-lg overflow-hidden" style={{ height: 60 }}>
                        <div className="w-full bg-orange-400 rounded-t-lg transition-all" style={{ height: `${pct}%`, marginTop: `${100 - pct}%` }} />
                      </div>
                      <p className="text-xs text-gray-400">{m.month}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Top clients */}
          {myStats.topClients?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Top Clients</p>
              <div className="space-y-2">
                {myStats.topClients.map((c: any, i: number) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <p className="text-sm font-medium text-navy-800 dark:text-white truncate">{c.name}</p>
                        <p className="text-xs text-gray-400 flex-shrink-0 ml-2">{c.jobs} jobs</p>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-full h-1.5">
                        <div className="bg-orange-400 h-1.5 rounded-full"
                          style={{ width: `${Math.round((c.jobs / myStats.topClients[0].jobs) * 100)}%` }} />
                      </div>
                    </div>
                    <p className="text-xs font-semibold text-orange-500 flex-shrink-0">{c.credits} cr</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'performance' && <div>
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold text-navy-900">Performance</h1>
          {(canSeeAll || hasPermission('export_reports')) && (
            <div className="flex items-center gap-2">
            <div className="relative group/pdf">
              <button onClick={downloadPDF} className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13h6M9 17h3"/></svg>
              </button>
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 text-white text-[10px] px-2 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover/pdf:opacity-100 transition-opacity pointer-events-none">
                Download PDF Report
              </div>
            </div>
            <div className="relative group/xlsx">
              <button onClick={downloadXLSX} className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:text-green-600 hover:border-green-200 transition-colors">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </button>
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 text-white text-[10px] px-2 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover/xlsx:opacity-100 transition-opacity pointer-events-none">
                Download Excel
              </div>
            </div>
            </div>
          )}
        </div>
      </div>}

      {/* Staff Card Tab */}
      {activeTab === 'staffcard' && hasPermission('view_staff_performance_card') && (
        <div className="card p-6 space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <User size={16} className="text-orange-500" />
            <p className="font-display font-semibold text-navy-900 dark:text-white">Staff Performance Card</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="label">Staff Member</label>
              <select className="input text-sm" value={scStaff} onChange={e => setScStaff(e.target.value)}>
                <option value="">Select staff...</option>
                {staffList.map((s: any) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Month</label>
              <select className="input text-sm" value={scMonth} onChange={e => setScMonth(parseInt(e.target.value))}>
                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                  <option key={i+1} value={i+1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Year</label>
              <select className="input text-sm" value={scYear} onChange={e => setScYear(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button
                disabled={!scStaff || scLoading}
                onClick={async () => {
                  setScLoading(true)
                  try {
                    const res = await api.get(`/performance/staff-card?userId=${scStaff}&month=${scMonth}&year=${scYear}`)
                    setScData(res.data)
                    setScOpen(true)
                  } catch { }
                  setScLoading(false)
                }}
                className="w-full btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                {scLoading ? 'Loading...' : 'View Card'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FILTERS */}
      {activeTab === 'performance' && <div className="space-y-6">
      <div className="card p-5 space-y-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filters</p>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={setToday}
            className="text-xs bg-navy-800 text-white px-4 py-2 rounded-lg font-medium hover:bg-navy-700 transition-colors">
            Today
          </button>
          <button onClick={setLast30}
            className="text-xs bg-gray-100 text-gray-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors">
            Last 30 Days
          </button>
          <div className="flex items-center gap-2">
            <input type="date" value={from}
              onChange={e => { setFrom(e.target.value); setPage(1); setErrorPage(1) }}
              className="text-xs border border-gray-200 rounded-lg px-3 py-2 text-gray-600 focus:outline-none focus:border-orange-300" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={to}
              onChange={e => { setTo(e.target.value); setPage(1); setErrorPage(1) }}
              className="text-xs border border-gray-200 rounded-lg px-3 py-2 text-gray-600 focus:outline-none focus:border-orange-300" />
          </div>
          {(from || to) && (
            <button onClick={() => { setFrom(''); setTo(''); setPage(1); setErrorPage(1) }}
              className="text-xs text-red-400 hover:text-red-600 font-medium">Clear</button>
          )}
        </div>
        {canSeeAll && staffList.length > 0 && (
          <div>
            <p className="text-xs text-gray-400 mb-2">Filter by Staff</p>
            <select
              value={selectedStaff[0] || ''}
              onChange={e => { setSelectedStaff(e.target.value ? [e.target.value] : []); setPage(1) }}
              className="input py-2 text-sm w-56">
              <option value="">All Staff</option>
              {staffList.map((s: any) => (
                <option key={s.id} value={s.id}>{s.name} {s.username ? `(@${s.username})` : ''}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* INFO BOXES */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="card p-5 flex items-center gap-4">
            <div className="bg-blue-50 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Target size={18} className="text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold text-navy-900">{summary.avgTargetPct}%</p>
              <p className="text-xs text-gray-400">Avg Target Achievement</p>
              <p className="text-xs text-gray-400 mt-0.5">avg {summary.avgNet} credits/day · target {summary.dailyTarget}</p>
            </div>
          </div>
          <div className="card p-5 flex items-center gap-4">
            <div className="bg-red-50 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={18} className="text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold text-navy-900">{summary.avgLossPct}%</p>
              <p className="text-xs text-gray-400">Average Loss</p>
              <p className="text-xs text-gray-400 mt-0.5">net {summary.totalNet} of {summary.totalGross} gross</p>
            </div>
          </div>
        </div>
      )}

      {/* BAR CHART */}
      {chartData.length > 0 && (
        <div className="card p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Target Achievement % by Day</p>
          <div className="overflow-x-auto">
            <svg width={chartTotalW} height={chartH + 50} className="block">
              {/* Grid lines at 0%, 25%, 50%, 75%, 100%, 125% */}
              {[0, 25, 50, 75, 100, 125].map(pct => {
                const y = chartH - (pct / 125) * chartH + 10
                return (
                  <g key={pct}>
                    <line x1={42} y1={y} x2={chartTotalW - 10} y2={y}
                      stroke={pct === 100 ? '#FF7F32' : '#f0f0f0'}
                      strokeWidth={pct === 100 ? 1.5 : 1}
                      strokeDasharray={pct === 100 ? '4 3' : undefined} />
                    <text x={38} y={y + 4} textAnchor="end" fontSize={9} fill={pct === 100 ? '#FF7F32' : '#aaa'}>
                      {pct}%
                    </text>
                  </g>
                )
              })}
              {chartData.map((d: any, i: number) => {
                const x = 48 + i * (barW + 6)
                const pct = Math.min(parseFloat(d.targetPct || 0), 125)
                const barH = Math.max((pct / 125) * chartH, 1)
                const isAbove = pct >= 100
                return (
                  <g key={d.date}>
                    <rect x={x} y={chartH - barH + 10} width={barW} height={barH}
                      fill={isAbove ? '#22c55e' : '#FF7F32'} rx={2} opacity={0.85} />
                    <text x={x + barW / 2} y={chartH + 26} textAnchor="middle" fontSize={8} fill="#999"
                      transform={`rotate(-40, ${x + barW / 2}, ${chartH + 26})`}>
                      {d.date.slice(5)}
                    </text>
                  </g>
                )
              })}
            </svg>
          </div>
          <div className="flex items-center gap-4 mt-1">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-green-500 rounded-sm" /><span className="text-xs text-gray-500">≥ 100% Target</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-orange-400 rounded-sm" /><span className="text-xs text-gray-500">&lt; 100% Target</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 border border-orange-400 rounded-sm" style={{borderStyle:'dashed'}} /><span className="text-xs text-gray-500">100% line</span></div>
          </div>
        </div>
      )}

      {/* DAILY LIST */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-display font-semibold text-navy-900">Daily Performance</h2>
        </div>
        {loading ? (
          <div className="space-y-2 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3 rounded-xl">
                <div className="skeleton h-4 w-24" />
                <div className="flex-1 skeleton h-3 rounded-full" />
                <div className="skeleton h-4 w-16" />
              </div>
            ))}
          </div>
        ) : dailyList.length === 0 ? (
          <div className="py-12 text-center text-gray-400">No data for selected period</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50/70 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Date</th>
                  {canSeeAll && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Staff</th>}
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Credits</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Target %</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Loss</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {dailyList.map((d: any, i: number) => (
                  <tr key={i} className="hover:bg-orange-50/30 cursor-pointer transition-colors" onClick={() => setSelectedDay(d)}>
                    <td className="px-5 py-3.5 text-sm font-medium text-navy-800">
                      {new Date(d.date + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </td>
                    {canSeeAll && (
                      <td className="px-5 py-3.5">
                        <p className="text-sm text-navy-700">{d.staffName}</p>
                        <p className="text-xs text-gray-400">@{d.staffUsername}</p>
                      </td>
                    )}
                    <td className="px-5 py-3.5 text-right hidden sm:table-cell">
                      <p className="font-semibold text-navy-800">{d.netCredits}</p>
                      <p className="text-xs text-gray-400">gross {d.grossCredits}</p>
                    </td>
                    <td className="px-5 py-3.5 text-right hidden sm:table-cell">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        parseFloat(d.targetPct) >= 100 ? 'bg-green-100 text-green-700' :
                        parseFloat(d.targetPct) >= 50 ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-700'}`}>{d.targetPct}%</span>
                    </td>
                    <td className="px-5 py-3.5 text-right text-xs font-medium text-red-500 hidden sm:table-cell">
                      {parseFloat(d.loss) > 0 ? `-${d.loss}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pagination && pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">{pagination.total} days — page {pagination.page} of {pagination.pages}</p>
                <div className="flex gap-2">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                    className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                    <ChevronLeft size={14} />
                  </button>
                  <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}
                    className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Day Jobs Modal */}
      {selectedDay && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedDay(null)}>
          <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
              <div>
                <p className="font-display font-bold text-navy-900 dark:text-white">
                  {new Date(selectedDay.date + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedDay.netCredits} credits · {selectedDay.targetPct}% target
                  {canSeeAll && selectedDay.staffName && ` · ${selectedDay.staffName}`}
                </p>
              </div>
              <button onClick={() => setSelectedDay(null)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-gray-50 dark:divide-[#1e3a5f]">
              {selectedDay.jobs?.length === 0 ? (
                <div className="py-10 text-center text-gray-400 text-sm">No job details available</div>
              ) : selectedDay.jobs?.map((j: any) => (
                <div key={j.jobId} className="px-6 py-3.5 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-navy-800 dark:text-white truncate">{j.displayTitle || j.jobCode}</p>
                    <p className="text-xs text-gray-400 font-mono">{j.jobCode}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {Math.round(Number(j.netMinutes))} min
                      {j.penaltyApplied && <span className="text-red-400 ml-1">· penalty applied</span>}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="text-sm font-bold text-navy-800 dark:text-white">{j.netCredits} cr</p>
                    {j.penaltyApplied && <p className="text-xs text-gray-400 line-through">{j.grossCredits}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ERROR & CORRECTION HISTORY */}
      
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-display font-semibold text-red-600">Error & Correction History</h2>
        </div>
        {pagedErrors.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-sm">No errors or corrections recorded</div>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {pagedErrors.map((e: any) => (
                <div key={e.id} className={`px-6 py-4 flex items-start gap-4 ${e.decision === 'error' ? 'bg-red-50/30' : 'bg-orange-50/30'}`}>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 mt-0.5 ${
                    e.decision === 'error' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
                  }`}>{e.decision === 'error' ? '✗ Error' : '↩ Correction'}</span>
                  <div className="flex-1">
                    {canSeeAll && e.artistName && <p className="text-xs font-semibold text-navy-700 mb-0.5">{e.artistName}</p>}
                    <p className="text-sm font-medium text-navy-800">{e.jobTitle}</p>
                    <p className="text-xs font-mono text-gray-400 mb-1">{e.jobCode}</p>
                    {e.feedback && (
                      <p className="text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-[#112240] rounded-lg px-3 py-2 border border-gray-100 dark:border-[#1e3a5f]">{e.feedback}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 flex-shrink-0">
                    {new Date(e.reviewedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              ))}
            </div>
            {errorPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">{errorTotal} records — page {errorPage} of {errorPages}</p>
                <div className="flex gap-2">
                  <button disabled={errorPage === 1} onClick={() => setErrorPage(p => p - 1)}
                    className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                    <ChevronLeft size={14} />
                  </button>
                  <button disabled={errorPage >= errorPages} onClick={() => setErrorPage(p => p + 1)}
                    className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      </div>}

      {/* Staff Card Modal */}
      {scOpen && scData && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setScOpen(false)}>
          <div className="bg-white dark:bg-[#0d1e35] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div className="rounded-t-2xl px-6 py-4 flex items-center justify-between flex-shrink-0 border-b border-gray-100 dark:border-[#1e3a5f] bg-white dark:bg-[#0d1e35]">
              <div className="flex items-center gap-3">
                <img src="/Full_Color_Logo.svg" className="h-6 object-contain block dark:hidden" alt="Inkcredible" />
                <img src="/Logo_Black Bg_Horizontal.png" alt="Inkcredible" className="h-6 object-contain hidden dark:block" />
                <div className="w-px h-6 bg-white/20" />
                <div>
                  <p className="text-navy-900 dark:text-white font-bold text-sm">{scData.staff.name}</p>
                  <p className="text-gray-400 text-xs capitalize">{scData.staff.role} · {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][scData.period.month - 1]} {scData.period.year}</p>
                  {scData.skillTags && scData.skillTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {scData.skillTags.map((s: any, i: number) => (
                        <span key={i} className="text-[10px] bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full font-medium">
                          {s.tag} · {s.count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {hasPermission('export_reports') && <button onClick={async () => {
                  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
                  const pageW = doc.internal.pageSize.getWidth()
                  const s = scData.summary
                  const monthName = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][scData.period.month - 1]

                  // Navy header
                  doc.setFillColor(10, 22, 40)
                  doc.rect(0, 0, pageW, 28, 'F')

                  // Logo
                  try {
                    const logoRes = await fetch('/Logo_Black Bg_Horizontal.png')
                    const blob = await logoRes.blob()
                    const logoBase64 = await new Promise<string>(resolve => {
                      const reader = new FileReader()
                      reader.onloadend = () => resolve(reader.result as string)
                      reader.readAsDataURL(blob)
                    })
                    doc.addImage(logoBase64, 'PNG', 10, 8, 44, 44 * (50/201))
                  } catch {}

                  // Header text
                  doc.setTextColor(255, 255, 255)
                  doc.setFontSize(13)
                  doc.setFont('helvetica', 'bold')
                  doc.text('Staff Performance Card', pageW - 10, 12, { align: 'right' })
                  doc.setFontSize(9)
                  doc.setFont('helvetica', 'normal')
                  doc.setTextColor(180, 180, 180)
                  doc.text(`${scData.staff.name} · ${monthName} ${scData.period.year}`, pageW - 10, 19, { align: 'right' })
                  doc.text(`Generated ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`, pageW - 10, 24, { align: 'right' })

                  let y = 36

                  // Section A — Summary
                  doc.setFontSize(9)
                  doc.setFont('helvetica', 'bold')
                  doc.setTextColor(249, 115, 22)
                  doc.text('MONTH SUMMARY', 10, y)
                  y += 4
                  autoTable(doc, {
                    startY: y,
                    head: [['Metric', 'Value']],
                    body: [
                      ['Total Net Credits', `${s.totalNet} cr`],
                      ['Total Gross Credits', `${s.totalGross} cr`],
                      ['Days Worked', `${s.daysWorked} days`],
                      ['Target Hit Rate', `${s.targetHitRate}% (${s.targetHitDays}/${s.daysWorked} days)`],
                      ['Avg Target Achieved', `${s.avgTargetAchieved}%`],
                      ['Best Day', `${s.bestDay.net} cr on ${s.bestDay.date}`],
                      ['Daily Target', `${s.dailyTarget} credits`],
                      ['Current Streak', `${s.currentStreak} days`],
                      ['Best Streak', `${s.bestStreak} days`],
                    ],
                    styles: { fontSize: 9, cellPadding: 3 },
                    headStyles: { fillColor: [10, 22, 40], textColor: 255 },
                    columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold', textColor: [80, 80, 80] }, 1: { cellWidth: 100 } },
                    margin: { left: 10, right: 10 },
                    theme: 'grid'
                  })
                  y = (doc as any).lastAutoTable.finalY + 8

                  // Section B — Quality
                  doc.setFontSize(9)
                  doc.setFont('helvetica', 'bold')
                  doc.setTextColor(249, 115, 22)
                  doc.text('QUALITY SCORE', 10, y)
                  y += 4
                  autoTable(doc, {
                    startY: y,
                    head: [['Metric', 'Value']],
                    body: [
                      ['Avg Efficiency', `${s.avgEfficiency}%`],
                      ['Credit Loss', `${s.totalLoss} cr (${s.creditLossPct}%)`],
                      ['Errors / Corrections', `${s.errorCount}`],
                      ['Penalty Rate', `${s.penaltyRate}%`],
                    ],
                    styles: { fontSize: 9, cellPadding: 3 },
                    headStyles: { fillColor: [10, 22, 40], textColor: 255 },
                    columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold', textColor: [80, 80, 80] }, 1: { cellWidth: 100 } },
                    margin: { left: 10, right: 10 },
                    theme: 'grid'
                  })
                  y = (doc as any).lastAutoTable.finalY + 8

                  // Section C — Client Breakdown
                  if (scData.clientBreakdown.length > 0) {
                    doc.setFontSize(9)
                    doc.setFont('helvetica', 'bold')
                    doc.setTextColor(249, 115, 22)
                    doc.text('CLIENT BREAKDOWN', 10, y)
                    y += 4
                    autoTable(doc, {
                      startY: y,
                      head: [['Client', 'Jobs', 'Net Credits', '% of Total']],
                      body: scData.clientBreakdown.map((c: any) => [c.name, c.jobs, `${c.net} cr`, `${c.pct}%`]),
                      styles: { fontSize: 9, cellPadding: 3 },
                      headStyles: { fillColor: [10, 22, 40], textColor: 255 },
                      margin: { left: 10, right: 10 },
                      theme: 'grid'
                    })
                    y = (doc as any).lastAutoTable.finalY + 8
                  }

                  // Section D — Peer Comparison
                  const validPeers = scData.peerComparison.filter((p: any) => p.peerPct !== null)
                  if (validPeers.length > 0) {
                    doc.setFontSize(9)
                    doc.setFont('helvetica', 'bold')
                    doc.setTextColor(249, 115, 22)
                    doc.text('PEER COMPARISON (vs team avg per job)', 10, y)
                    y += 4
                    autoTable(doc, {
                      startY: y,
                      head: [['Client', 'My Avg/Day', 'Team Avg/Day', 'vs Peers']],
                      body: validPeers.map((p: any) => [p.clientName, `${p.myAvg} cr`, `${p.peerAvg} cr`, `${p.peerPct}%`]),
                      styles: { fontSize: 9, cellPadding: 3 },
                      headStyles: { fillColor: [10, 22, 40], textColor: 255 },
                      margin: { left: 10, right: 10 },
                      theme: 'grid'
                    })
                  }

                  // Footer
                  doc.setFontSize(8)
                  doc.setTextColor(150, 150, 150)
                  doc.setFont('helvetica', 'normal')
                  doc.text('Inkcredible Studios · Confidential · For internal use only', pageW / 2, 287, { align: 'center' })

                  doc.save(`Performance_Card_${scData.staff.name.replace(/\s+/g, '_')}_${monthName}_${scData.period.year}.pdf`)
                }}
                  className="flex items-center gap-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  PDF
                </button>}
                <button onClick={() => setScOpen(false)} className="text-gray-400 hover:text-gray-600 dark:text-white/60 dark:hover:text-white"><X size={16} /></button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 p-6 space-y-5">

              {/* Section A — Summary */}
              <div>
                <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3">Month Summary</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { label: 'Net Credits', value: `${scData.summary.totalNet}`, sub: `of ${scData.summary.totalGross} gross` },
                    { label: 'Days Worked', value: scData.summary.daysWorked, sub: 'days with submissions' },
                    { label: 'Target Hit Rate', value: `${scData.summary.targetHitRate}%`, sub: `${scData.summary.targetHitDays}/${scData.summary.daysWorked} days` },
                    { label: 'Avg Target Achieved', value: `${scData.summary.avgTargetAchieved}%`, sub: 'avg of daily target %' },
                    { label: 'Best Day(cr)', value: `${scData.summary.bestDay.net}`, sub: scData.summary.bestDay.date || '—' },
                  ].map(item => (
                    <div key={item.label} className="bg-gray-50 dark:bg-[#1a3050] rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                      <p className="text-xl font-bold text-navy-900 dark:text-white leading-none">{item.value}</p>
                      <p className="text-xs text-gray-400 mt-1">{item.sub}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5 bg-orange-50 dark:bg-orange-900/10 px-3 py-1.5 rounded-lg">
                    <Flame size={13} className="text-orange-500" />
                    <span className="text-xs font-semibold text-orange-600">{scData.summary.currentStreak} day streak</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-yellow-50 dark:bg-yellow-900/10 px-3 py-1.5 rounded-lg">
                    <Trophy size={13} className="text-yellow-500" />
                    <span className="text-xs font-semibold text-yellow-600">Target hit: {scData.summary.targetHitDays} days</span>
                  </div>
                </div>
              </div>

              {/* Section B — Quality */}
              <div>
                <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3">Quality Score</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-1">Avg Efficiency</p>
                    <p className="text-xl font-bold text-blue-600 leading-none">{scData.summary.avgEfficiency}%</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/10 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-1">Credit Loss</p>
                    <p className="text-xl font-bold text-red-500 leading-none">{scData.summary.creditLossPct}%</p>
                    <p className="text-xs text-gray-400 mt-1">{scData.summary.totalLoss} cr lost</p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/10 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-1">Errors / Corrections</p>
                    <p className="text-xl font-bold text-orange-500 leading-none">{scData.summary.errorCount}</p>
                  </div>
                </div>
              </div>

              {/* Section C — Client Breakdown */}
              {scData.clientBreakdown.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3">Client Breakdown</p>
                  <div className="space-y-2">
                    {scData.clientBreakdown.map((c: any) => (
                      <div key={c.clientId} className="flex items-center gap-3">
                        <p className="text-xs font-medium text-navy-800 dark:text-white w-24 flex-shrink-0 truncate">{c.name}</p>
                        <div className="flex-1 bg-gray-100 dark:bg-[#1a3050] rounded-full h-2">
                          <div className="bg-orange-400 h-2 rounded-full transition-all" style={{ width: `${c.pct}%` }} />
                        </div>
                        <p className="text-xs text-gray-400 w-16 text-right flex-shrink-0">{c.pct}% · {c.net}cr</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Section D — Peer Comparison */}
              {scData.peerComparison.filter((p: any) => p.peerPct !== null).length > 0 && (
                <div>
                  <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3">Peer Comparison</p>
                  <div className="space-y-2">
                    {scData.peerComparison.filter((p: any) => p.peerPct !== null).map((p: any, i: number) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-[#1a3050] rounded-xl px-4 py-3">
                        <p className="text-xs font-medium text-navy-800 dark:text-white">{p.clientName}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span>My avg: <span className="font-semibold text-navy-700 dark:text-white">{p.myAvg} cr/day</span></span>
                        <span>Team avg: <span className="font-semibold text-navy-700 dark:text-white">{p.peerAvg} cr/day</span></span>
                          <span className={`font-bold px-2 py-0.5 rounded-full ${p.peerPct >= 100 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'}`}>
                            {p.peerPct}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Section E — Daily Trend Sparkline */}
              {scData.dailyTrend.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3">Daily Trend</p>
                  <div className="bg-gray-50 dark:bg-[#1a3050] rounded-xl p-4">
                    <svg width="100%" height="80" viewBox={`0 0 ${scData.dailyTrend.length * 14} 70`} preserveAspectRatio="xMidYMid meet">
                      {(() => {
                        const maxNet = Math.max(...scData.dailyTrend.map((d: any) => d.net), scData.summary.dailyTarget)
                        const toY = (val: number) => 58 - (val / maxNet) * 50
                        const targetY = toY(scData.summary.dailyTarget)
                        const totalW = scData.dailyTrend.length * 14
                        const points = scData.dailyTrend.map((d: any, i: number) => {
                          const x = i * 14 + 7
                          const y = toY(d.net)
                          return `${x},${y}`
                        }).join(' ')
                        return (
                          <>
                            {/* 0 baseline */}
                            <line x1={0} y1={59} x2={totalW} y2={59} stroke="#e5e7eb" strokeWidth="1" />
                            {/* 100% target line */}
                            <line x1={0} y1={targetY} x2={totalW} y2={targetY} stroke="#f97316" strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />
                            <text x={totalW - 2} y={targetY - 2} fontSize="7" fill="#f97316" textAnchor="end" opacity="0.8">target</text>
                            {/* Trend line */}
                            <polyline points={points} fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            {/* Dots */}
                            {scData.dailyTrend.map((d: any, i: number) => {
                              const x = i * 14 + 7
                              const y = toY(d.net)
                              return <circle key={i} cx={x} cy={y} r="2.5" fill={d.targetPct >= 100 ? '#22c55e' : '#f97316'} />
                            })}
                          </>
                        )
                      })()}
                    </svg>
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-xs text-gray-400">Target met</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-orange-400" /><span className="text-xs text-gray-400">Below target</span></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Section F — Errors */}
              {scData.errors.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-3">Errors & Corrections</p>
                  <div className="space-y-2">
                    {scData.errors.map((e: any) => (
                      <div key={e.id} className={`rounded-xl px-4 py-3 flex items-start gap-3 ${e.decision === 'error' ? 'bg-red-50 dark:bg-red-900/10' : 'bg-orange-50 dark:bg-orange-900/10'}`}>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${e.decision === 'error' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                          {e.decision === 'error' ? 'Error' : 'Correction'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-navy-800 dark:text-white truncate">{e.jobTitle}</p>
                          <p className="text-xs font-mono text-gray-400">{e.jobCode}</p>
                          {e.feedback && <p className="text-xs text-gray-500 mt-1">{e.feedback}</p>}
                        </div>
                        <p className="text-xs text-gray-400 flex-shrink-0">{new Date(e.reviewedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* LEADERBOARD HISTORY */}
      {activeTab === 'history' && <LeaderboardHistory />}
    </div>
  )
}
